import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Typography,
  Divider,
} from '@mui/material';
import { SmartToy, ExpandMore, Refresh } from '@mui/icons-material';
import { CircularProgress } from '@mui/material';
import { AI_PHASES, AI_PHASE_NAMES, AI_TASK_PLANNER, AI_PLAN_STATUS } from '../../shared/appConstants';

import { renderHtmlContent } from '../../shared/htmlUtils';
import AIPromptTracker from './AIPromptTracker';
import TaskPreviewDialog from './TaskPreviewDialog';
import AIDevTabStepper from './AIDevTabStepper';
import { useDispatch, useSelector } from 'react-redux';
import { generateAIPlan, fetchAIPlanByTaskId, fetchAIPlanDropdown, updateAIPlan, updateAIPlanApproval, fetchAICodeGeneration, createTicketComment, fetchTicketComments } from '../../store';
import { toast } from 'react-toastify';
import './AIDeveloperComponents.scss';

const AITaskPlanner = ({ ticketId, ticketUuid, taskData: propTaskData }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { user } = useSelector(state => state.auth);
  const [showPreview, setShowPreview] = useState(false);
  const [loading, setLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [generatedPlan, setGeneratedPlan] = useState(null);
  const [aiPromptData, setAiPromptData] = useState([]);

  const [codeGenData, setCodeGenData] = useState(null);
  const [expandedStep, setExpandedStep] = useState(0);
  const [taskData, setTaskData] = useState({
    title: '',
    description: '',
    acceptanceCriteria: ''
  });
  const [descriptionExpanded, setDescriptionExpanded] = useState(false);
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [selectedRepository, setSelectedRepository] = useState('');
  const [repositories, setRepositories] = useState([]);
  const [approvalRepositories, setApprovalRepositories] = useState([]);
  const [planData, setPlanData] = useState(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [refreshLoading, setRefreshLoading] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [editedTasks, setEditedTasks] = useState({});
  const [isEditingAllTasks, setIsEditingAllTasks] = useState(false);
  const [expandedAccordion, setExpandedAccordion] = useState(0);
  const [deletedTasks, setDeletedTasks] = useState(new Set());

  // Helper function to build prompt history from API data
  const buildPromptHistoryFromAPI = (apiData) => {
    const promptHistory = [];
    
    // Plan Generation phase
    if (apiData?.plan?.prompt_text) {
      promptHistory.push({
        phase: AI_PHASES.PLANNING,
        phaseName: AI_PHASE_NAMES[AI_PHASES.PLANNING],
        prompts: [{
          id: 'plan-1',
          name: 'Plan Generation',
          type: 'Plan Generator Template',
          content: apiData.plan.prompt_text
        }]
      });
    }
    
    // Code Generation phase
    if (apiData?.approval?.codeGenerations?.length > 0) {
      const codeGenPrompts = [];
      apiData.approval.codeGenerations.forEach((codeGen, index) => {
        if (codeGen.prompt_text) {
          codeGenPrompts.push({
            id: `code-${codeGen.id || index}`,
            name: `Code Generation ${index + 1}`,
            type: 'Code Generation Template',
            content: codeGen.prompt_text
          });
        }
      });
      
      if (codeGenPrompts.length > 0) {
        promptHistory.push({
          phase: AI_PHASES.CODE_GENERATION,
          phaseName: AI_PHASE_NAMES[AI_PHASES.CODE_GENERATION],
          prompts: codeGenPrompts
        });
      }
    }
    
    return promptHistory;
  };


  useEffect(() => {
    if (ticketUuid) {
      window.ticketUuid = ticketUuid;
      // Fetch existing plan data when component loads
      fetchExistingPlan();
    } else {
      setInitialLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketUuid]);

  const fetchExistingPlan = async (isRefresh = false) => {
    if (!ticketId) {
      setInitialLoading(false);
      return;
    }
    
    if (isRefresh) setRefreshLoading(true);
    
    try {
      const result = await dispatch(fetchAIPlanByTaskId(ticketId)).unwrap();
      if (result.data) {
        setPlanData(result.data);
        setGeneratedPlan({ apiData: result.data });
        // Set current step based on approval status
        if (result.data?.approval?.status === 'APPROVED') {
          setCurrentStep(1);
          setExpandedStep(1);
          // Fetch code generation data when plan is approved
          fetchCodeGenerationData();
          // Fetch repository data if repo_details is null
          if (!result.data.approval.repo_details) {
            fetchApprovalRepositories();
          }
        } else {
          setCurrentStep(0);
          setExpandedStep(0);
        }
        // Set prompt data from API response
        setAiPromptData(buildPromptHistoryFromAPI(result.data));
      }
    } catch (error) {
      setPlanData(null);
      setGeneratedPlan(null);
      setAiPromptData([]);
      setCurrentStep(0);
      setExpandedStep(0);
    } finally {
      setInitialLoading(false);
      if (isRefresh) setRefreshLoading(false);
    }
  };

  const fetchCodeGenerationData = async () => {
    try {
      const result = await dispatch(fetchAICodeGeneration(ticketId)).unwrap();
      if (result.data) {
        setCodeGenData(result.data);
        // Update prompt history with code generation data
        if (planData) {
          const combinedData = {
            ...planData,
            approval: {
              ...planData.approval,
              codeGenerations: result.data?.approval?.codeGenerations || []
            }
          };
          setAiPromptData(buildPromptHistoryFromAPI(combinedData));
        }
        // Check if code generation is complete
        const hasApproval = result.data?.approval;
        const hasCodeGen = result.data?.approval?.codeGenerations?.length > 0;
        const hasPRs = result.data?.approval?.codeGenerations?.[0]?.pullRequests?.length > 0;
        if (hasApproval && hasCodeGen && hasPRs) {
          setCurrentStep(2);
          setExpandedStep(2);
        }
      }
    } catch (error) {
      setCodeGenData(null);
    }
  };

  const fetchApprovalRepositories = async () => {
    const projectId = propTaskData?.project?.id || propTaskData?.project_id;
    if (projectId) {
      try {
        const result = await dispatch(fetchAIPlanDropdown({ projectId, type: 'project_repository' })).unwrap();
        const repos = result.data?.project_repository || [];
        setApprovalRepositories(repos);
      } catch (error) {
        setApprovalRepositories([]);
      }
    }
  };

  const handleShowPreview = async () => {
    // Use actual task data from props
    if (propTaskData) {
      setTaskData({
        title: propTaskData.title || 'Untitled Task',
        description: propTaskData.description || '',
        acceptanceCriteria: propTaskData.acceptanceCriteria || propTaskData.acceptance_criteria || 'No acceptance criteria provided'
      });
    }
    
    // Load repositories from API
    const projectId = propTaskData?.project?.id || propTaskData?.project_id;
    if (projectId) {
      try {
        const result = await dispatch(fetchAIPlanDropdown({ projectId, type: 'project_repository' })).unwrap();
        const repos = result.data?.project_repository || [];
        setRepositories(repos);
        setSelectedRepository(repos[0]?.key || '');
      } catch (error) {
        setRepositories([]);
        setSelectedRepository('');
      }
    }
    
    setShowPreview(true);
  };

  const handleCancelPreview = () => {
    setShowPreview(false);
  };

  // Helper function to check if description is empty or contains only HTML tags
  const isDescriptionEmpty = (description) => {
    if (!description || description.trim() === '') {
      return true;
    }
    
    // Decode HTML entities first, then remove HTML tags
    const decoded = description.replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&amp;/g, '&').replace(/&quot;/g, '"').replace(/&#39;/g, "'");
    const textContent = decoded.replace(/<[^>]*>/g, '').trim();
    const isEmpty = textContent === '';
    return isEmpty;
  };

  const handleStartPlanning = async () => {
    // Validate description and repository selection
    if (isDescriptionEmpty(taskData.description)) {
      toast.error(t('aiTaskPlanner.validation.descriptionRequired'));
      return;
    }
    
    if (!selectedRepository) {
      toast.error(t('aiTaskPlanner.validation.repositoryRequired'));
      return;
    }
    
    setLoading(true);
    try {
      // Find the selected repository object
      const selectedRepoObj = repositories.find(repo => repo.key === selectedRepository);
      
      // Call the API to create plan
      const payload = {
        external_task_id: ticketId,
        project_id: propTaskData?.project?.id || propTaskData?.project_id,
        title: taskData.title,
        description: taskData.description,
        repo_details: selectedRepoObj?.value || selectedRepoObj || null
      };
      
      const createResult = await dispatch(generateAIPlan(payload)).unwrap();
      
      if (createResult.data?.external_task_id) {
        const planResult = await dispatch(fetchAIPlanByTaskId(createResult.data.external_task_id)).unwrap();
        setPlanData(planResult.data);
        setGeneratedPlan({ apiData: planResult.data });
        
        // Add comment that AI dev bot started and plan generation is in progress
        const userName = `${user?.first_name || ''} ${user?.last_name || ''}`.trim() || 'User';
        try {
          await dispatch(createTicketComment({ 
            ticketId: ticketId, 
            comment: `${userName} ${t('aiTaskPlanner.comments.aiDevBotStarted')} ${t('aiTaskPlanner.comments.planGenerationInProgress')}`,
            attachments: [],
            suppressToast: true
          })).unwrap();
          // Refresh comments to show the new comment
          dispatch(fetchTicketComments(ticketId));
        } catch (commentError) {
          //ignore
        }
        
        // Set current step based on approval status
        if (planResult.data?.approval?.status === AI_PLAN_STATUS.APPROVED) {
          setCurrentStep(1);
          setExpandedStep(1);
        } else {
          setCurrentStep(0);
          setExpandedStep(0);
        }
        // Set prompt data from API response
        setAiPromptData(buildPromptHistoryFromAPI(planResult.data));
        setShowPreview(false);
        toast.success(t('aiTaskPlanner.messages.planGenerationStarted'));
      }
    } catch (error) {
      toast.error(t('aiTaskPlanner.messages.planGenerationFailed'));
    } finally {
      setLoading(false);
    }
  };

  // Handle plan approval and update UI state
  const handleApprovePlan = async () => {
    setLoading(true);
    try {
      if (planData?.approval?.id && planData?.plan?.id) {
        await dispatch(updateAIPlanApproval({
          approvalId: planData.approval.id,
          status: AI_PLAN_STATUS.APPROVED,
          planId: planData.plan.id
        })).unwrap();
        
        // Add approval comment to ticket
        const userName = `${user?.first_name || ''} ${user?.last_name || ''}`.trim() || 'User';
        const currentTime = new Date().toLocaleString();
        try {
          await dispatch(createTicketComment({ 
            ticketId: ticketId, 
            comment: `${t('aiTaskPlanner.comments.planApproved')} ${userName} ${t('aiTaskPlanner.comments.at')} ${currentTime}`,
            attachments: [],
            suppressToast: true
          })).unwrap();
          dispatch(fetchTicketComments(ticketId));
        } catch (commentError) {
          // Ignore comment errors to not block approval flow
        }
        
        // Refresh data and fetch code generation info
        await fetchExistingPlan(true);
        await fetchCodeGenerationData();
        toast.success(t('aiTaskPlanner.messages.planApproved'));
      }
      setCurrentStep(1);
      setExpandedStep(1);
    } catch (error) {
      toast.error(t('aiTaskPlanner.messages.planApprovalFailed'));
    } finally {
      setLoading(false);
    }
  };



  // Initialize single task editing mode
  const handleEditTask = (taskId) => {
    if (isEditingAllTasks) return; // Prevent individual edit when global edit is active
    setEditingTask(taskId);
    const task = planData.plan.plan_json.plan.tasks.find(t => t.id === taskId);
    setEditedTasks(prev => ({
      ...prev,
      [taskId]: {
        title: task.title || '',
        description: task.description || '',
        files: [...(task.files || [])],
        acceptanceCriteria: [...(task.acceptanceCriteria || [])]
      }
    }));
  };

  // Initialize bulk editing mode for all tasks
  const handleEditAllTasks = () => {
    setIsEditingAllTasks(true);
    setEditingTask(null);
    setExpandedAccordion(AI_TASK_PLANNER.EXPANDED_ALL);
    // Prepare editable state for all tasks
    const allTasksEdited = {};
    planData.plan.plan_json.plan.tasks.forEach(task => {
      allTasksEdited[task.id] = {
        title: task.title || '',
        description: task.description || '',
        files: [...(task.files || [])],
        acceptanceCriteria: [...(task.acceptanceCriteria || [])]
      };
    });
    setEditedTasks(allTasksEdited);
  };

  // Save all edited tasks and exit bulk edit mode
  const handleSaveAllTasks = async () => {
    try {
      setLoading(true);
      // Apply edits and deletions to all tasks
      const updatedTasks = planData.plan.plan_json.plan.tasks
        .filter(task => !deletedTasks.has(task.id))
        .map(task => {
          if (editedTasks[task.id]) {
            return {
              ...task,
              title: editedTasks[task.id].title,
              description: editedTasks[task.id].description,
              files: editedTasks[task.id].files,
              acceptanceCriteria: editedTasks[task.id].acceptanceCriteria
            };
          }
          return task;
        });
      
      await dispatch(updateAIPlan({ id: planData.plan.id, task: updatedTasks })).unwrap();
      // Reset editing state
      setIsEditingAllTasks(false);
      setEditedTasks({});
      setDeletedTasks(new Set());
      setExpandedAccordion(0);
      await fetchExistingPlan(true);
      toast.success(t('aiTaskPlanner.messages.allTasksUpdated'));
    } catch (error) {
      toast.error(t('aiTaskPlanner.messages.allTasksUpdateFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleCancelAllTasks = () => {
    setIsEditingAllTasks(false);
    setEditedTasks({});
    setDeletedTasks(new Set());
    setExpandedAccordion(0);
  };

  // Delete individual task
  const handleDeleteTask = (taskId) => {
    const remainingTasks = planData.plan.plan_json.plan.tasks.filter(task => !deletedTasks.has(task.id) && task.id !== taskId);
    if (remainingTasks.length === 0) {
      toast.error(t('aiTaskPlanner.messages.cannotDeleteLastTask'));
      return;
    }
    setDeletedTasks(prev => new Set([...prev, taskId]));
  };

  // Restore deleted task
  const handleRestoreTask = (taskId) => {
    setDeletedTasks(prev => {
      const newSet = new Set(prev);
      newSet.delete(taskId);
      return newSet;
    });
  };

  // Save individual task changes
  const handleSaveTask = async (taskId) => {
    try {
      setLoading(true);
      // Apply changes and deletions
      const updatedTasks = planData.plan.plan_json.plan.tasks
        .filter(task => !deletedTasks.has(task.id))
        .map(task => {
          if (task.id === taskId && editedTasks[taskId]) {
            return {
              ...task,
              title: editedTasks[taskId].title,
              description: editedTasks[taskId].description,
              files: editedTasks[taskId].files,
              acceptanceCriteria: editedTasks[taskId].acceptanceCriteria
            };
          }
          return task;
        });
      
      await dispatch(updateAIPlan({ id: planData.plan.id, task: updatedTasks })).unwrap();
      // Clear editing state for this task
      setEditingTask(null);
      setEditedTasks(prev => {
        const newState = { ...prev };
        delete newState[taskId];
        return newState;
      });
      setDeletedTasks(new Set());
      await fetchExistingPlan(true);
      toast.success(t('aiTaskPlanner.messages.taskUpdated'));
    } catch (error) {
      toast.error(t('aiTaskPlanner.messages.taskUpdateFailed'));
    } finally {
      setLoading(false);
    }
  };


  if (initialLoading || refreshLoading) {
    return (
      <Box className="ai-task-planner">
        <Box className="task-planner-container">
          <Typography>{t('aiTaskPlanner.loading')}</Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box className="ai-task-planner">
      <Box className="task-planner-container">
      {/* Initial state - show start button */}
      {!generatedPlan && !showPreview ? (
        <Box className="start-container">
          <Button
            fullWidth
            variant="contained"
            startIcon={<SmartToy />}
            onClick={handleShowPreview}
            className="start-button"
          >
            {t('aiTaskPlanner.start.button')}
          </Button>
          <Typography variant="body2" color="text.secondary" className="start-description">
            {t('aiTaskPlanner.start.description')}
          </Typography>
        </Box>
      ) : showPreview ? (
        <TaskPreviewDialog
          taskData={taskData}
          selectedRepository={selectedRepository}
          setSelectedRepository={setSelectedRepository}
          repositories={repositories}
          isEditingDescription={isEditingDescription}
          setIsEditingDescription={setIsEditingDescription}
          setTaskData={setTaskData}
          isDescriptionEmpty={isDescriptionEmpty}
          handleCancelPreview={handleCancelPreview}
          handleStartPlanning={handleStartPlanning}
          loading={loading}
        />
      ) : (
        <Box className="main-content">
          {/* Header with task title, description and refresh */}
          <Box className="content-header">
            <Box className="header-row">
              <Typography variant="h5" className="title">
                {planData?.title || taskData.title}
              </Typography>
              <Button
                startIcon={refreshLoading ? <CircularProgress size={16} /> : <Refresh />}
                onClick={() => fetchExistingPlan(true)}
                disabled={refreshLoading}
                variant="outlined"
                size="small"
                className="refresh-button"
              >
                {t('aiTaskPlanner.header.refresh')}
              </Button>
            </Box>
            
            <Box className="description-section">
              <Typography variant="subtitle1" className="section-title">
                {t('aiTaskPlanner.header.description')}
              </Typography>
              <Box className="description-box">
                <Box className={`description-text ${descriptionExpanded ? 'expanded' : ''}`}>
                  <div dangerouslySetInnerHTML={{ __html: renderHtmlContent(planData?.description || taskData.description) }} />
                </Box>
                {(planData?.description || taskData.description) && (planData?.description || taskData.description).length > 200 && (
                  <Button
                    size="small"
                    onClick={() => setDescriptionExpanded(!descriptionExpanded)}
                    startIcon={descriptionExpanded ? <ExpandMore sx={{ transform: 'rotate(180deg)' }} /> : <ExpandMore />}
                    className="read-more-button"
                  >
                    {descriptionExpanded ? t('aiTaskPlanner.header.readLess') : t('aiTaskPlanner.header.readMore')}
                  </Button>
                )}
              </Box>
            </Box>
            
            <Divider />
          </Box>

          <AIDevTabStepper
            currentStep={currentStep}
            expandedStep={expandedStep}
            setExpandedStep={setExpandedStep}
            planData={planData}
            codeGenData={codeGenData}
            approvalRepositories={approvalRepositories}
            loading={loading}
            editingTask={editingTask}
            editedTasks={editedTasks}
            isEditingAllTasks={isEditingAllTasks}
            expandedAccordion={expandedAccordion}
            setExpandedAccordion={setExpandedAccordion}
            handleApprovePlan={handleApprovePlan}
            handleEditTask={handleEditTask}
            handleEditAllTasks={handleEditAllTasks}
            handleSaveAllTasks={handleSaveAllTasks}
            handleCancelAllTasks={handleCancelAllTasks}
            handleSaveTask={handleSaveTask}
            handleDeleteTask={handleDeleteTask}
            handleRestoreTask={handleRestoreTask}
            deletedTasks={deletedTasks}
            setEditedTasks={setEditedTasks}
            setEditingTask={setEditingTask}
            setCurrentStep={setCurrentStep}
          />

          {/* Show AI prompt history if available */}
          {aiPromptData.length > 0 && (
            <Box>
              <Typography variant="h6" className="prompt-history-title">
                {t('aiTaskPlanner.promptHistory.title')}
              </Typography>
              <AIPromptTracker promptData={aiPromptData} compact={true} />
            </Box>
          )}
        </Box>
      )}
      </Box>
    </Box>
  );
};

export default AITaskPlanner;